import { Progress } from '@1trading/react/components/ui/progress';
import { cn } from '@1trading/react/lib/utils';
import type React from 'react';
import { useTranslation } from 'react-i18next';

import { useProgress } from '@/context/progress';

const ProgressBar = () => {
  const { steps } = useProgress();
  const { t } = useTranslation();

  return (
    <nav aria-label={t('fundamentals.progress')} className="flex justify-center gap-4 xl:gap-8 w-full max-w-2xl mx-auto">
      {steps.map((step, index) => {
        const progressPercentage = Math.round(step.progress * 100);
        const gradientStartPosition = steps.length === 1 ? 0 : index * (100 / (steps.length - 1));
        const gradientEndPosition = steps.length === 1 ? 100 : index === steps.length - 1 ? 100 : (index + 1) * (100 / (steps.length - 1));

        return (
          <section
            key={step.title}
            className="flex-1 flex flex-col items-center justify-center gap-2 max-w-40 text-center"
            aria-label={`${t('fundamentals.step')} ${index + 1}: ${step.title}`}
            style={
              {
                '--progress-gradient-from': `color-mix(in hsl, hsl(var(--primary-gradient-from)) ${100 - gradientStartPosition}%, hsl(var(--primary-gradient-to)) ${gradientStartPosition}%)`,
                '--progress-gradient-to': `color-mix(in hsl, hsl(var(--primary-gradient-from)) ${100 - gradientEndPosition}%, hsl(var(--primary-gradient-to)) ${gradientEndPosition}%)`,
              } as React.CSSProperties
            }
          >
            <Progress
              className="-md:*:supports-color-mix:from-[color:var(--progress-gradient-from)] -md:*:supports-color-mix:to-[color:var(--progress-gradient-to)]"
              size="sm"
              variant="primary-gradient"
              value={progressPercentage}
              aria-valuenow={progressPercentage}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${step.title} ${t('fundamentals.progress')}: ${progressPercentage}%`}
            />
            <span
              className={cn('text-sm font-medium line-clamp-1 break-all -md:sr-only', step.isActive || step.progress === 1 ? 'text-foreground' : 'text-border')}
              aria-current={step.isActive ? 'step' : undefined}
            >
              {step.title}
            </span>
          </section>
        );
      })}
    </nav>
  );
};

export default ProgressBar;
